import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, Store, Users, TrendingUp, LogOut, Shield, MessageSquare, Package, CreditCard
} from 'lucide-react';

const ALL_NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/sad/dashboard', page: 'dashboard' },
  { label: 'Catalog', icon: Package, path: '/sad/catalog', page: 'catalog' },
  { label: 'Subscriptions', icon: CreditCard, path: '/sad/subscriptions', page: 'subscriptions' },
  { label: 'Shops', icon: Store, path: '/sad/shops', page: 'shops' },
  { label: 'Users', icon: Users, path: '/sad/users', page: 'users' },
  { label: 'Revenue', icon: TrendingUp, path: '/sad/revenue', page: 'revenue' },
  { label: 'Tickets', icon: MessageSquare, path: '/sad/tickets', page: 'tickets' },
];

const ADMIN_ROLES = ['super_admin', 'admin', 'accountant', 'support_agent', 'content_manager', 'personal_assistant'];

const ROLE_PAGES = {
  super_admin: ['dashboard', 'catalog', 'subscriptions', 'shops', 'users', 'revenue', 'tickets'],
  admin: ['dashboard', 'catalog', 'subscriptions', 'shops', 'users', 'revenue', 'tickets'],
  accountant: ['dashboard', 'revenue', 'subscriptions', 'shops'],
  support_agent: ['dashboard', 'shops', 'users', 'tickets'],
  content_manager: ['dashboard', 'catalog'],
  personal_assistant: ['dashboard', 'catalog', 'subscriptions', 'shops', 'users', 'revenue', 'tickets'],
};

const SuperAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState('super_admin');

  useEffect(() => {
    const token = localStorage.getItem('sad_token');
    const user = JSON.parse(localStorage.getItem('sad_user') || '{}');
    if (!token || !ADMIN_ROLES.includes(user.role)) {
      navigate('/sad/login');
    } else {
      setUserRole(user.role);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('sad_token');
    localStorage.removeItem('sad_user');
    navigate('/sad/login');
  };

  return (
    <div className="min-h-screen bg-[#0f172a] flex" data-testid="sad-layout">
      {/* Sidebar */}
      <aside className="w-56 bg-[#1e293b] border-r border-slate-700 flex flex-col">
        <div className="p-4 border-b border-slate-700 flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded flex items-center justify-center">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-white text-sm font-bold">VendFactura</div>
            <div className="text-amber-400 text-[10px] font-medium uppercase tracking-wider">Super Admin</div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          {ALL_NAV_ITEMS.filter(item => (ROLE_PAGES[userRole] || []).includes(item.page)).map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  active
                    ? 'bg-amber-500/10 text-amber-400 border-r-2 border-amber-500'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`sad-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-700">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-400 hover:text-red-400 text-sm w-full px-2 py-2"
            data-testid="sad-logout"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default SuperAdminLayout;
